/**
 * Tax calculation endpoints. (External-income withholding estimates are the
 * server's: externalIncomeApi.estimate, WEBD-02.)
 */

import {
  DEFAULT_HARVEST_TARGET,
  type TaxEstimationResult,
  type TaxLossHarvestPlan,
  type TaxLossHarvestPlanRequest,
} from '@luminaria/shared';
import type { FullTaxCalculationInput } from '../hooks/useTaxCalculation';
import { apiClient } from './client';
import { API_ENDPOINTS } from './endpoints';

interface Validate401kResult {
  valid: boolean;
  limit: number;
  excess: number;
}

/**
 * Every field `/tax/calculate-full` accepts, with the server's defaults
 * applied, so the query key and the POST body are the same object and two
 * spellings of one request (`priorYearTax: undefined` vs `0`) share one
 * cache entry (WEBD-01). (The route takes no lot-matching method.)
 */
export function normalizeTaxCalculationInput(input: FullTaxCalculationInput): NormalizedTaxCalculationInput {
  return {
    ...(input.portfolioId ? { portfolioId: input.portfolioId } : {}),
    taxYear: input.taxYear,
    filingStatus: input.filingStatus,
    state: input.state,
    priorYearTax: input.priorYearTax ?? 0,
    capitalLossCarryforward: input.capitalLossCarryforward ?? 0,
    projectFullYear: input.projectFullYear ?? false,
    includeOpenOptions: input.includeOpenOptions ?? false,
  };
}

type NormalizedTaxCalculationInput = Required<Omit<FullTaxCalculationInput, 'portfolioId'>> &
  Pick<FullTaxCalculationInput, 'portfolioId'>;

/**
 * The harvest-plan body with the server's defaults applied, so the query key
 * and the POST body are one object (WEBD-01).
 */
export function normalizeHarvestPlanRequest(input: TaxLossHarvestPlanRequest): NormalizedHarvestPlanRequest {
  return {
    portfolioId: input.portfolioId,
    target: input.target ?? DEFAULT_HARVEST_TARGET,
    minLossPerLot: input.minLossPerLot ?? 0,
    ...(input.filingStatus ? { filingStatus: input.filingStatus } : {}),
    ...(input.state ? { state: input.state } : {}),
    ...(input.priorYearTax !== undefined ? { priorYearTax: input.priorYearTax } : {}),
    ...(input.capitalLossCarryforward !== undefined ? { capitalLossCarryforward: input.capitalLossCarryforward } : {}),
    projectFullYear: input.projectFullYear ?? false,
    includeOpenOptions: input.includeOpenOptions ?? false,
  };
}

type NormalizedHarvestPlanRequest = TaxLossHarvestPlanRequest &
  Required<Pick<TaxLossHarvestPlanRequest, 'target' | 'minLossPerLot' | 'projectFullYear' | 'includeOpenOptions'>>;

export const taxApi = {
  async calculateFull(input: NormalizedTaxCalculationInput): Promise<TaxEstimationResult> {
    const { data } = await apiClient.post<TaxEstimationResult>(API_ENDPOINTS.TAX.CALCULATE_FULL, input);
    return data;
  },

  /** The lot-level tax-loss harvest plan (read-only, TLH-11). */
  async harvestPlan(input: NormalizedHarvestPlanRequest): Promise<TaxLossHarvestPlan> {
    const { data } = await apiClient.post<TaxLossHarvestPlan>(API_ENDPOINTS.TAX.HARVEST_PLAN, input);
    return data;
  },

  async validate401k(body: { annualContribution: number; isAge50OrOver: boolean }): Promise<Validate401kResult> {
    const { data } = await apiClient.post<Validate401kResult>(API_ENDPOINTS.TAX.VALIDATE_401K, body);
    return data;
  },
};
